import { Heart, Baby, BookOpen, Star, Shield, Smile, Moon, Sun, Music, Flower2 } from "lucide-react";
import { ReactNode } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

// Custom SVG icons not available in lucide
const BabyBottle = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10 2h4v3a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1V2z" />
    <path d="M8.5 6h7l.5 2H8l.5-2z" />
    <path d="M8 8h8v2a8 8 0 0 1-1.5 4.5L13 17v3a2 2 0 0 1-2 2h0a2 2 0 0 1-2-2v-3l-1.5-2.5A8 8 0 0 1 6 10V8h2z" />
    <line x1="8" y1="11" x2="16" y2="11" />
  </svg>
);

const Stroller = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="8" cy="20" r="2" />
    <circle cx="18" cy="20" r="2" />
    <path d="M12 2a1 1 0 0 1 1 1v3h5a2 2 0 0 1 2 2v6a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8a2 2 0 0 1 2-2h5V3a1 1 0 0 1 1-1z" />
    <path d="M6 10h12" />
  </svg>
);

interface FloatingIcon {
  icon: ReactNode;
  top: string;
  left: string;
  size: string;
  delay: string;
  duration: string;
  color: string;
}

const iconSets: FloatingIcon[][] = [
  // Set 0 - Hero
  [
    { icon: <Heart />, top: "6%", left: "5%", size: "w-5 h-5", delay: "0s", duration: "7s", color: "text-neon-pink" },
    { icon: <Baby />, top: "12%", left: "88%", size: "w-6 h-6", delay: "1.5s", duration: "8s", color: "text-neon-violet" },
    { icon: <BabyBottle />, top: "55%", left: "3%", size: "w-5 h-5", delay: "2s", duration: "9s", color: "text-neon-blue" },
    { icon: <Star />, top: "30%", left: "85%", size: "w-4 h-4", delay: "0.5s", duration: "6s", color: "text-neon-pink" },
    { icon: <Stroller />, top: "75%", left: "82%", size: "w-5 h-5", delay: "3s", duration: "8s", color: "text-neon-violet" },
    { icon: <Moon />, top: "65%", left: "90%", size: "w-4 h-4", delay: "4s", duration: "7s", color: "text-neon-blue" },
    { icon: <Smile />, top: "42%", left: "8%", size: "w-5 h-5", delay: "1s", duration: "8s", color: "text-neon-violet" },
    { icon: <BookOpen />, top: "22%", left: "12%", size: "w-4 h-4", delay: "3.5s", duration: "9s", color: "text-neon-blue" },
    { icon: <Flower2 />, top: "15%", left: "25%", size: "w-4 h-4", delay: "2.5s", duration: "7s", color: "text-neon-pink" },
    { icon: <Sun />, top: "50%", left: "78%", size: "w-4 h-4", delay: "0.8s", duration: "8s", color: "text-neon-violet" },
    { icon: <Shield />, top: "80%", left: "15%", size: "w-4 h-4", delay: "4.5s", duration: "9s", color: "text-neon-blue" },
    { icon: <Music />, top: "38%", left: "75%", size: "w-4 h-4", delay: "1.8s", duration: "6s", color: "text-neon-pink" },
    { icon: <Heart />, top: "70%", left: "22%", size: "w-4 h-4", delay: "3.2s", duration: "8s", color: "text-neon-violet" },
    { icon: <Baby />, top: "85%", left: "72%", size: "w-5 h-5", delay: "2.8s", duration: "7s", color: "text-neon-blue" },
  ],
  // Set 1 - Avantages
  [
    { icon: <Baby />, top: "10%", left: "6%", size: "w-5 h-5", delay: "0.5s", duration: "8s", color: "text-neon-violet" },
    { icon: <BabyBottle />, top: "65%", left: "88%", size: "w-5 h-5", delay: "2s", duration: "7s", color: "text-neon-pink" },
    { icon: <Heart />, top: "45%", left: "8%", size: "w-4 h-4", delay: "1s", duration: "9s", color: "text-neon-blue" },
    { icon: <Stroller />, top: "20%", left: "82%", size: "w-5 h-5", delay: "3.5s", duration: "8s", color: "text-neon-violet" },
    { icon: <Sun />, top: "78%", left: "10%", size: "w-4 h-4", delay: "0s", duration: "7s", color: "text-neon-pink" },
    { icon: <Shield />, top: "35%", left: "85%", size: "w-4 h-4", delay: "2.5s", duration: "9s", color: "text-neon-blue" },
    { icon: <Flower2 />, top: "55%", left: "5%", size: "w-5 h-5", delay: "4s", duration: "8s", color: "text-neon-violet" },
    { icon: <Star />, top: "15%", left: "22%", size: "w-4 h-4", delay: "1.5s", duration: "7s", color: "text-neon-pink" },
    { icon: <Moon />, top: "72%", left: "78%", size: "w-4 h-4", delay: "3s", duration: "8s", color: "text-neon-blue" },
    { icon: <BookOpen />, top: "30%", left: "18%", size: "w-4 h-4", delay: "0.8s", duration: "9s", color: "text-neon-violet" },
    { icon: <Smile />, top: "50%", left: "75%", size: "w-5 h-5", delay: "4.5s", duration: "7s", color: "text-neon-pink" },
    { icon: <Baby />, top: "85%", left: "68%", size: "w-4 h-4", delay: "2.2s", duration: "8s", color: "text-neon-blue" },
  ],
  // Set 2 - Comment ça marche
  [
    { icon: <Stroller />, top: "8%", left: "7%", size: "w-5 h-5", delay: "1s", duration: "9s", color: "text-neon-pink" },
    { icon: <BookOpen />, top: "60%", left: "86%", size: "w-4 h-4", delay: "0s", duration: "7s", color: "text-neon-blue" },
    { icon: <Baby />, top: "78%", left: "10%", size: "w-5 h-5", delay: "2.5s", duration: "8s", color: "text-neon-violet" },
    { icon: <BabyBottle />, top: "25%", left: "84%", size: "w-5 h-5", delay: "1.5s", duration: "6s", color: "text-neon-pink" },
    { icon: <Music />, top: "42%", left: "6%", size: "w-4 h-4", delay: "3s", duration: "8s", color: "text-neon-violet" },
    { icon: <Moon />, top: "15%", left: "80%", size: "w-4 h-4", delay: "0.5s", duration: "7s", color: "text-neon-blue" },
    { icon: <Star />, top: "70%", left: "88%", size: "w-4 h-4", delay: "3.5s", duration: "9s", color: "text-neon-pink" },
    { icon: <Heart />, top: "18%", left: "20%", size: "w-4 h-4", delay: "2s", duration: "7s", color: "text-neon-violet" },
    { icon: <Flower2 />, top: "50%", left: "78%", size: "w-5 h-5", delay: "4s", duration: "8s", color: "text-neon-blue" },
    { icon: <Shield />, top: "35%", left: "15%", size: "w-4 h-4", delay: "1.2s", duration: "9s", color: "text-neon-pink" },
    { icon: <Smile />, top: "85%", left: "75%", size: "w-4 h-4", delay: "3.8s", duration: "7s", color: "text-neon-violet" },
    { icon: <Sun />, top: "55%", left: "20%", size: "w-4 h-4", delay: "0.3s", duration: "8s", color: "text-neon-blue" },
  ],
  // Set 3 - Tarifs
  [
    { icon: <Heart />, top: "10%", left: "85%", size: "w-4 h-4", delay: "0.5s", duration: "7s", color: "text-neon-pink" },
    { icon: <Stroller />, top: "55%", left: "6%", size: "w-5 h-5", delay: "2s", duration: "8s", color: "text-neon-violet" },
    { icon: <Baby />, top: "30%", left: "82%", size: "w-5 h-5", delay: "3s", duration: "9s", color: "text-neon-blue" },
    { icon: <BabyBottle />, top: "75%", left: "10%", size: "w-4 h-4", delay: "1s", duration: "7s", color: "text-neon-pink" },
    { icon: <Smile />, top: "18%", left: "8%", size: "w-5 h-5", delay: "4s", duration: "8s", color: "text-neon-violet" },
    { icon: <Flower2 />, top: "48%", left: "88%", size: "w-4 h-4", delay: "1.5s", duration: "9s", color: "text-neon-blue" },
    { icon: <Sun />, top: "68%", left: "82%", size: "w-4 h-4", delay: "2.5s", duration: "6s", color: "text-neon-pink" },
    { icon: <Moon />, top: "22%", left: "20%", size: "w-4 h-4", delay: "3.5s", duration: "8s", color: "text-neon-violet" },
    { icon: <Star />, top: "40%", left: "15%", size: "w-4 h-4", delay: "0.8s", duration: "7s", color: "text-neon-blue" },
    { icon: <BookOpen />, top: "82%", left: "72%", size: "w-4 h-4", delay: "4.2s", duration: "9s", color: "text-neon-pink" },
    { icon: <Shield />, top: "60%", left: "78%", size: "w-5 h-5", delay: "1.8s", duration: "8s", color: "text-neon-violet" },
    { icon: <Music />, top: "85%", left: "25%", size: "w-4 h-4", delay: "3.2s", duration: "7s", color: "text-neon-blue" },
  ],
  // Set 4 - Preuve sociale
  [
    { icon: <Baby />, top: "12%", left: "8%", size: "w-5 h-5", delay: "0s", duration: "8s", color: "text-neon-violet" },
    { icon: <Stroller />, top: "45%", left: "86%", size: "w-5 h-5", delay: "2s", duration: "7s", color: "text-neon-pink" },
    { icon: <BabyBottle />, top: "68%", left: "6%", size: "w-4 h-4", delay: "1.5s", duration: "9s", color: "text-neon-blue" },
    { icon: <Star />, top: "25%", left: "82%", size: "w-4 h-4", delay: "3s", duration: "7s", color: "text-neon-violet" },
    { icon: <Moon />, top: "78%", left: "80%", size: "w-4 h-4", delay: "0.5s", duration: "8s", color: "text-neon-blue" },
    { icon: <Heart />, top: "38%", left: "5%", size: "w-4 h-4", delay: "4s", duration: "6s", color: "text-neon-pink" },
    { icon: <Flower2 />, top: "15%", left: "22%", size: "w-5 h-5", delay: "1.2s", duration: "8s", color: "text-neon-violet" },
    { icon: <Sun />, top: "55%", left: "78%", size: "w-4 h-4", delay: "2.8s", duration: "9s", color: "text-neon-blue" },
    { icon: <BookOpen />, top: "82%", left: "18%", size: "w-4 h-4", delay: "3.5s", duration: "7s", color: "text-neon-pink" },
    { icon: <Smile />, top: "50%", left: "20%", size: "w-4 h-4", delay: "0.8s", duration: "8s", color: "text-neon-violet" },
    { icon: <Shield />, top: "30%", left: "75%", size: "w-4 h-4", delay: "4.5s", duration: "9s", color: "text-neon-blue" },
    { icon: <Music />, top: "72%", left: "70%", size: "w-5 h-5", delay: "1.8s", duration: "7s", color: "text-neon-pink" },
  ],
  // Set 5 - CTA Final
  [
    { icon: <Heart />, top: "8%", left: "6%", size: "w-5 h-5", delay: "0.5s", duration: "7s", color: "text-neon-pink" },
    { icon: <Baby />, top: "78%", left: "86%", size: "w-6 h-6", delay: "1s", duration: "8s", color: "text-neon-violet" },
    { icon: <Stroller />, top: "42%", left: "8%", size: "w-5 h-5", delay: "2.5s", duration: "9s", color: "text-neon-blue" },
    { icon: <BabyBottle />, top: "25%", left: "82%", size: "w-4 h-4", delay: "3s", duration: "7s", color: "text-neon-pink" },
    { icon: <BookOpen />, top: "62%", left: "6%", size: "w-4 h-4", delay: "0s", duration: "8s", color: "text-neon-violet" },
    { icon: <Shield />, top: "18%", left: "80%", size: "w-4 h-4", delay: "4s", duration: "9s", color: "text-neon-blue" },
    { icon: <Flower2 />, top: "50%", left: "85%", size: "w-5 h-5", delay: "1.5s", duration: "7s", color: "text-neon-pink" },
    { icon: <Star />, top: "15%", left: "20%", size: "w-4 h-4", delay: "2s", duration: "8s", color: "text-neon-violet" },
    { icon: <Moon />, top: "70%", left: "75%", size: "w-4 h-4", delay: "3.5s", duration: "6s", color: "text-neon-blue" },
    { icon: <Sun />, top: "35%", left: "18%", size: "w-4 h-4", delay: "0.8s", duration: "9s", color: "text-neon-pink" },
    { icon: <Smile />, top: "85%", left: "22%", size: "w-5 h-5", delay: "4.2s", duration: "8s", color: "text-neon-violet" },
    { icon: <Music />, top: "55%", left: "72%", size: "w-4 h-4", delay: "1.2s", duration: "7s", color: "text-neon-blue" },
  ],
];

interface SectionFloatingIconsProps {
  variant?: number;
}

const SectionFloatingIcons = ({ variant = 0 }: SectionFloatingIconsProps) => {
  const { getSetting } = useSiteSettings();

  const enabled = getSetting("floating_icons_enabled", "true") === "true";
  const opacity = parseInt(getSetting("floating_icons_opacity", "50")) / 100;

  if (!enabled) return null;

  const icons = iconSets[variant % iconSets.length];

  return (
    <>
      {icons.map((item, i) => (
        <div
          key={i}
          className={`absolute ${item.size} ${item.color} animate-float-drift pointer-events-none`}
          style={{
            top: item.top,
            left: item.left,
            animationDelay: item.delay,
            animationDuration: item.duration,
            opacity,
            zIndex: 1,
          }}
          aria-hidden="true"
        >
          {item.icon}
        </div>
      ))}
    </>
  );
};

export default SectionFloatingIcons;
